import { get } from 'lodash';
import { FormattedMessage } from 'react-intl';
import { IconCheck } from '@rossum/ui/icons/tabler';
import { Box, Divider, Stack, SvgIcon, Typography } from '@rossum/ui/material';
import {
  AUTOMATION_PROCESS,
  FIELDS_VALUE_NOT_FOUND,
} from '../../../../constants/values';
import { Chunks } from '../../../../lib/formaterValues';
import {
  AnyDatapointDataST,
  ValidationSources,
} from '../../../../types/datapoints';
import TooltipItem from '../../DatapointTooltip/components/TooltipItem';
import { irrelevantTickInfo } from '../../helpers';
import styles from '../../style.module.sass';
import KeyboardShortcutsInfo from './KeyboardShortcutsInfo';

type ValidationSourcesContentProps = {
  data: AnyDatapointDataST;
  documentAutomated?: boolean;
};

export const ValidationSourcesContent = ({
  data,
  documentAutomated,
}: ValidationSourcesContentProps) => {
  const validationSources = get(
    data,
    'validationSources',
    []
  ) as Array<ValidationSources>;

  const confidenceScore =
    'content' in data ? data.content?.rirConfidence : undefined;

  const validatedByHuman = validationSources.includes('human');

  const validatedByOtherSources = validationSources.filter(
    source => source !== 'human'
  );

  return (
    <Stack divider={<Divider />} spacing={1.5} sx={{ pb: 1 }}>
      {validatedByHuman && (
        <Stack direction="row" alignItems="center" gap={1}>
          <SvgIcon fontSize="medium" color="success">
            <IconCheck />
          </SvgIcon>
          <Typography variant="body2">
            <FormattedMessage id="components.documentValidation.validationSources.tooltip.human" />
          </Typography>
        </Stack>
      )}
      {validatedByOtherSources.length > 0 &&
        !irrelevantTickInfo(validatedByOtherSources, documentAutomated) && (
          <Stack spacing={1}>
            <Typography variant="body2" fontSize={13}>
              <FormattedMessage
                id="components.documentValidation.validationSources.tooltip.otherSources.title"
                values={{
                  white: (chunks: Chunks[]) => (
                    <span style={{ fontWeight: 'bold' }}>{chunks}</span>
                  ),
                }}
              />
            </Typography>
            {validatedByOtherSources.map(validationSource => {
              switch (validationSource) {
                case 'connector':
                case 'non_required':
                case 'additional_value':
                case 'default_value':
                case 'history':
                case 'data_matching':
                case 'checks':
                case 'table_suggester':
                  return (
                    <TooltipItem
                      key={validationSource}
                      icon={
                        <SvgIcon fontSize="medium">
                          <IconCheck />
                        </SvgIcon>
                      }
                      localeKey={`components.documentValidation.validationSources.tooltip.${validationSource}`}
                      helpLink={AUTOMATION_PROCESS}
                    />
                  );

                case 'not_found':
                  return documentAutomated ? (
                    <TooltipItem
                      key={validationSource}
                      icon={
                        <SvgIcon fontSize="medium">
                          <IconCheck />
                        </SvgIcon>
                      }
                      localeKey="components.documentValidation.validationSources.tooltip.not_found"
                      helpLink={FIELDS_VALUE_NOT_FOUND}
                    />
                  ) : null;

                case 'score':
                  return (
                    <TooltipItem
                      key={validationSource}
                      icon={
                        <SvgIcon fontSize="medium">
                          <IconCheck />
                        </SvgIcon>
                      }
                      localeKey="components.documentValidation.validationSources.tooltip.score"
                      helpLink={AUTOMATION_PROCESS}
                    >
                      <Box className={styles['ConfidenceScore']}>
                        {confidenceScore?.toFixed(3) ?? '---'}
                      </Box>
                    </TooltipItem>
                  );

                default:
                  return (
                    <TooltipItem
                      key={validationSource}
                      icon={
                        <SvgIcon fontSize="medium">
                          <IconCheck />
                        </SvgIcon>
                      }
                      localeKey="components.documentValidation.validationSources.tooltip.other"
                      helpLink={AUTOMATION_PROCESS}
                    />
                  );
              }
            })}
          </Stack>
        )}
      <KeyboardShortcutsInfo />
    </Stack>
  );
};

type ValidationSourcesIconProps = {
  inFooter: boolean;
  data: AnyDatapointDataST;
  tickIconColor?: 'green' | 'white' | undefined;
};

export const ValidationSourcesIcon = ({
  inFooter,
  tickIconColor: color,
  data,
}: ValidationSourcesIconProps) => {
  const isSidebarDp = get(data, ['meta', 'sidebarDatapoint']);

  return (
    <SvgIcon
      fontSize="large"
      sx={{
        width: inFooter ? 16 : 24,
        height: inFooter ? 16 : 24,
        cursor: inFooter || isSidebarDp ? 'auto' : 'pointer',
      }}
      color={color === 'green' ? 'success' : 'inherit'}
    >
      <IconCheck />
    </SvgIcon>
  );
};
